import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback
} from 'react';
import { useNotif } from 'src/hooks';
import { useFRcamera } from '../../useAiCamera';

const FaceRegistrationContext = createContext({
  mode: 1,
  aiBox: false,
  aiLandmark: false,
  showGuideSquare: false,
  imgCount: 5,
  webcamRef: null,
  canvasRef: null,
  capturedImage: null,
  setCapturedImage: () => {},
  capture: () => {},
  bioID: '',
  setBioID: () => {},
  faceDetected: {
    face: false,
    details: { confidence: 0, name: '' }
  },
  loadingModels: false,
  registeredFaces: [],
  setRegisteredFaces: () => {},
  handleRegisterFace: () => {},
  handleSaveImages: () => {},
  IDLabel: '',
  setIDLabel: () => {},
  register: false,
  setRegister: () => {},
  data: {}, //From Field component
  onChangeField: () => {}, // From Field component,
  saved: false,
  setSaved: () => {},
  req: {},
  imgs: [],
  handleSetBioID: () => {},
  dataImgs: [],
  setDataImgs: () => {}
});

const capturePositions = ['Front', 'Left', 'Right', 'Up', 'Down'];

function FaceRegistrationProvider({ children }) {
  const { onChangeField, settings, data } = useFRcamera();
  const { imgCount = 5 } = data || settings || {};

  const [registeredFaces, setRegisteredFaces] = useState([]);
  const [bioID, setBioID] = useState(null);
  const [saved, setSaved] = useState(false);

  const notiffy = useNotif();
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);

  const compressImageToTargetSize = async (input, maxSize = 0, quality = 0) => {
    const loadImage = src =>
      new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => resolve(img);
        img.onerror = reject;

        img.src = src;
      });

    const getDataUrl = (img, size, quality) => {
      const originalWidth = img.naturalWidth || img.width;
      const originalHeight = img.naturalHeight || img.height;

      const scale = Math.min(1, size / Math.max(originalWidth, originalHeight));

      const width = Math.round(originalWidth * scale);
      const height = Math.round(originalHeight * scale);

      const canvas = document.createElement('canvas');

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');

      ctx.drawImage(img, 0, 0, width, height);

      return canvas.toDataURL('image/jpeg', quality);
    };

    let img;

    if (input instanceof File || input instanceof Blob) {
      const reader = new FileReader();

      const base64 = await new Promise(resolve => {
        reader.onload = e => resolve(e.target.result);
        reader.readAsDataURL(input);
      });

      img = await loadImage(base64);
    } else {
      img = await loadImage(input);
    }

    return getDataUrl(img, maxSize, quality);
  };

  const getBase64SizeKB = base64 => {
    const base64Data = base64.split(',')[1] || base64;
    return (base64Data.length * 3) / 4 / 1024;
  };

  const processImages = async files => {
    const targetKB = 800;
    const targetBytes = targetKB * 1024;

    const images = files.slice(0, 5);

    let quality = 0.7;
    let maxSize = 225;
    let results = [];

    const getBase64Size = base64 => {
      const base64Data = base64.split(',')[1] || base64;
      const padding = (base64Data.match(/=*$/) || [''])[0].length;

      return Math.ceil((base64Data.length * 3) / 4 - padding);
    };

    const getTotalSize = imgs =>
      imgs.reduce((total, img) => total + getBase64Size(img), 0);

    for (let attempt = 0; attempt < 30; attempt++) {
      results = [];

      for (const file of images) {
        const img = await compressImageToTargetSize(file, maxSize, quality);

        results.push(img);
      }

      const totalBytes = getTotalSize(results);
      const totalKB = totalBytes / 1024;

      console.log(
        `Attempt ${attempt + 1}:`,
        `${totalKB.toFixed(2)} KB`,
        `Quality: ${quality.toFixed(2)}`,
        `Size: ${maxSize}`
      );

      // Close enough: within 2 KB
      if (Math.abs(totalBytes - targetBytes) <= 2 * 1024) {
        break;
      }

      if (totalBytes < targetBytes) {
        // Increase quality first
        if (quality < 1) {
          quality = Math.min(1, quality + 0.05);
        } else {
          // Quality is already maximum,
          // so increase resolution
          maxSize += 25;
        }
      } else {
        // Too large
        if (quality > 0.2) {
          quality -= 0.05;
        } else {
          maxSize -= 25;
        }
      }
    }

    return results;
  };

  const handleSaveImages = async () => {
    const compressedImages = await processImages(registeredFaces);

    onChangeField({
      target: {
        name: data.fld,
        value: {
          id: bioID,
          images: compressedImages
        }
      }
    });

    setSaved(true);
  };

  const handleRegisterFace = useCallback(() => {
    const maxImages = imgCount || 5;

    const img = webcamRef.current?.getScreenshot();

    if (!img) return;

    setRegisteredFaces(prev => {
      if (prev.length >= maxImages) {
        notiffy.warning(`Already captured ${maxImages} images.`);

        return prev;
      }
      const updated = [...prev, img];

      if (updated.length === maxImages) {
        notiffy.success('Face registration complete!');
      }

      return updated;
    });
  }, [imgCount, notiffy]);

  return (
    <FaceRegistrationContext.Provider
      value={{
        webcamRef,
        canvasRef,
        handleRegisterFace,
        registeredFaces,
        handleSaveImages,
        bioID,
        setBioID,
        data,
        imgCount,
        capturePositions
      }}
    >
      {children}
    </FaceRegistrationContext.Provider>
  );
}

export const useFaceRegistration = () => {
  const ctx = useContext(FaceRegistrationContext);
  if (!ctx) throw new Error('useFRcamera must be used within provider');
  return ctx;
};

export default FaceRegistrationProvider;
